/**
 * Types mirroring docs/API.md. The frontend renders what the API says and
 * never recomputes scores, split grams or completeness.
 */

export type SectionKind = 'h' | 'p' | 'r';
export type Theme = 'dark' | 'light';

export type ExerciseType = 'Jumppa' | 'Kävely' | 'Kuntosali' | 'Juoksu' | 'Pyöräily' | 'Muu';
export const EXERCISE_TYPES: ExerciseType[] = ['Jumppa', 'Kävely', 'Kuntosali', 'Juoksu', 'Pyöräily', 'Muu'];

export interface Exercise {
    type: ExerciseType;
    minutes: number | null;
}

/* ---- plans ---- */

export interface PlanItem {
    id: string;
    name: string;
    grams: number;
    gramsCooked: number | null;
}

export interface PlanSection {
    id: string;
    kind: SectionKind;
    label: string;
    items: PlanItem[];
}

export interface PlanMeal {
    id: string;
    mealNo: number;
    name: string;
    hint: string;
    vegTargetG: number | null;
    sections: PlanSection[];
}

export interface Plan {
    id: string;
    title: string;
    subtitle: string;
    isDefault: boolean;
    meals: PlanMeal[];
}

/* ---- settings ---- */

export interface Settings {
    planId: string;
    theme: Theme;
}

/* ---- day ---- */

export interface DayItem extends PlanItem {
    selected: boolean;
    gramsEffective: number;
    gramsCookedEffective: number | null;
}

export interface DaySection {
    id: string;
    kind: SectionKind;
    label: string;
    filled: boolean;
    items: DayItem[];
}

export interface DayMeal {
    mealNo: number;
    planMealId: string;
    name: string;
    hint: string;
    vegTargetG: number | null;
    vegDone: boolean;
    complete: boolean;
    sectionsFilled: number;
    sectionsTotal: number;
    photoUrl: string | null;
    sections: DaySection[];
}

export interface Day {
    date: string;
    planId: string;
    exists: boolean;
    weightKg: number | null;
    exercise: Exercise | null;
    score: number;
    meals: DayMeal[];
}

export interface DayPatch {
    weightKg?: number | null;
    exercise?: { type: ExerciseType; minutes?: number | null } | null;
}

export interface MealSelection {
    itemIds: string[];
    vegDone: boolean;
}

export interface PhotoUrl {
    uploadUrl: string;
    key: string;
}

/* ---- calendar ---- */

export interface CalendarDay {
    date: string;
    score: number;
    touched: boolean;
    exercise: boolean;
}

export interface CalendarSummary {
    fullDays: number;
    exerciseDays: number;
    loggedDays: number;
}

export interface CalendarMonth {
    month: string;
    days: CalendarDay[];
    summary: CalendarSummary;
}

/* ---- progress ---- */

export interface WeightPoint {
    date: string;
    kg: number;
}

export interface ProgressWeek {
    weekStart: string;
    isoWeek: number;
    daysElapsed: number;
    fullDays: number;
    exerciseDays: number;
}

export interface Progress {
    today: string;
    streaks: { fullDays: number; exerciseDays: number };
    exerciseLast7: number;
    weights: WeightPoint[];
    weeks: ProgressWeek[];
    last30: { fullDays: number; exerciseDays: number; loggedDays: number };
}

/* ---- errors ---- */

export interface ApiErrorBody {
    error: string;
    details?: { field: string; message: string }[];
}
